/*
  Onyx colour chart: search, filters and sorting, one-tap add to cart, the inline shade panel
  (similar shades by measured colour, restock email for sold-out shades) and the offer ladder.
  Markup: sections/onyx-swatch-wall.liquid and snippets/onyx-chart-tile.liquid.
*/
(() => {
  if (customElements.get('onyx-colour-chart')) return;

  const GROUPS = ['family', 'finish', 'occasion'];
  const GROUP_PARAMS = { family: 'c', finish: 'f', occasion: 'o' };
  const SORTS = ['number', 'light', 'dark', 'hue', 'newest'];
  const CART_SECTIONS = [
    { id: 'mini-cart', section: 'mini-cart', selector: '.shopify-section' },
    { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    { id: 'mobile-cart-icon-bubble', section: 'mobile-cart-icon-bubble', selector: '.shopify-section' },
  ];

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehaviour = () => (reducedMotion() ? 'instant' : 'smooth');

  // Replaces [name] placeholders in a translated string.
  const fill = (template, values) =>
    Object.entries(values).reduce((text, [key, value]) => text.split(`[${key}]`).join(String(value)), template || '');

  // Lowercase, without accents, Greek final sigma folded, so "κοκκινο" finds "Κόκκινο".
  const fold = (text) =>
    String(text || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ς/g, 'σ')
      .toLowerCase()
      .trim();

  const withoutLeadingZeros = (text) => text.replace(/^0+(?=\d)/, '');

  const cartRoute = (path) => {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return `${root}${path}`.replace(/\/{2,}/g, '/');
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const resizeImage = (src, size) =>
    src.replace(/([?&])width=\d+/, `$1width=${size}`).replace(/([?&])height=\d+/, `$1height=${size}`);

  // sRGB hex to CIELAB (D65), for colour sorting and similarity.
  function hexToLab(hex) {
    const match = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
    if (!match) return null;
    const value = parseInt(match[1], 16);
    const [r, g, b] = [(value >> 16) & 255, (value >> 8) & 255, value & 255].map((channel) => {
      const c = channel / 255;
      return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    const x = (r * 0.4124564 + g * 0.3575761 + b * 0.1804375) / 0.95047;
    const y = r * 0.2126729 + g * 0.7151522 + b * 0.072175;
    const z = (r * 0.0193339 + g * 0.119192 + b * 0.9503041) / 1.08883;
    const f = (t) => (t > 216 / 24389 ? Math.cbrt(t) : (24389 / 27 * t + 16) / 116);
    const fx = f(x);
    const fy = f(y);
    const fz = f(z);
    return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
  }

  // CIEDE2000 colour difference: how different two shades look to the eye.
  function deltaE([L1, a1, b1], [L2, a2, b2]) {
    const rad = Math.PI / 180;
    const pow7 = (v) => Math.pow(v, 7);
    const C1 = Math.hypot(a1, b1);
    const C2 = Math.hypot(a2, b2);
    const Cbar = (C1 + C2) / 2;
    const G = 0.5 * (1 - Math.sqrt(pow7(Cbar) / (pow7(Cbar) + pow7(25))));
    const a1p = (1 + G) * a1;
    const a2p = (1 + G) * a2;
    const C1p = Math.hypot(a1p, b1);
    const C2p = Math.hypot(a2p, b2);
    const hue = (b, a) => {
      if (a === 0 && b === 0) return 0;
      const h = Math.atan2(b, a) / rad;
      return h >= 0 ? h : h + 360;
    };
    const h1p = hue(b1, a1p);
    const h2p = hue(b2, a2p);
    const dLp = L2 - L1;
    const dCp = C2p - C1p;
    let dhp = 0;
    if (C1p * C2p !== 0) {
      dhp = h2p - h1p;
      if (dhp > 180) dhp -= 360;
      else if (dhp < -180) dhp += 360;
    }
    const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((dhp / 2) * rad);
    const Lbarp = (L1 + L2) / 2;
    const Cbarp = (C1p + C2p) / 2;
    let hbarp = h1p + h2p;
    if (C1p * C2p !== 0) {
      if (Math.abs(h1p - h2p) > 180) hbarp += h1p + h2p < 360 ? 360 : -360;
      hbarp /= 2;
    }
    const T =
      1 -
      0.17 * Math.cos((hbarp - 30) * rad) +
      0.24 * Math.cos(2 * hbarp * rad) +
      0.32 * Math.cos((3 * hbarp + 6) * rad) -
      0.2 * Math.cos((4 * hbarp - 63) * rad);
    const dTheta = 30 * Math.exp(-Math.pow((hbarp - 275) / 25, 2));
    const RC = 2 * Math.sqrt(pow7(Cbarp) / (pow7(Cbarp) + pow7(25)));
    const SL = 1 + (0.015 * Math.pow(Lbarp - 50, 2)) / Math.sqrt(20 + Math.pow(Lbarp - 50, 2));
    const SC = 1 + 0.045 * Cbarp;
    const SH = 1 + 0.015 * Cbarp * T;
    const RT = -Math.sin(2 * dTheta * rad) * RC;
    return Math.sqrt(
      Math.pow(dLp / SL, 2) + Math.pow(dCp / SC, 2) + Math.pow(dHp / SH, 2) + RT * (dCp / SC) * (dHp / SH)
    );
  }

  class OnyxColourChart extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.i18n = this.readJSON(this.querySelector('[data-i18n]'));
      this.lang = this.dataset.lang || document.documentElement.lang || 'en';
      this.plurals = new Intl.PluralRules(this.lang);
      this.collator = new Intl.Collator(this.lang, { numeric: true, sensitivity: 'base' });
      this.soldOutMode = this.dataset.soldOut || 'last';
      this.similarCount = Number(this.dataset.similarCount) || 0;
      this.notifyTag = (this.dataset.notifyTag || 'restock').trim();
      this.cartQty = new Map();
      this.productCache = new Map();
      this.notified = new Set();

      this.grid = this.querySelector('[data-grid]');
      this.live = this.querySelector('[data-live]');
      this.toast = this.querySelector('[data-toast]');
      this.topButton = this.querySelector('[data-top]');
      this.notifyForm = this.querySelector('.cc-notify-proxy');

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));

      this.setupOffer();
      if (this.grid) this.setupChart();
      this.setupScroll();
      this.setupToast();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === 'onyx-colour-chart') return;
          if (event && event.cart && Array.isArray(event.cart.items)) this.applyCart(event.cart);
          else this.fetchCart();
        });
      }
      this.onPageShow = (event) => {
        if (event.persisted) this.fetchCart();
      };
      window.addEventListener('pageshow', this.onPageShow);
      this.fetchCart();

      const params = new URLSearchParams(window.location.search);
      if (params.get('customer_posted') === 'true' && window.location.hash.startsWith('#CcNotify-')) {
        this.showToast(this.i18n.notifyDone, { cart: false });
      }
    }

    disconnectedCallback() {
      if (this.unsubscribeCart) this.unsubscribeCart();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.headerObserver) this.headerObserver.disconnect();
      if (this.headerResizeObserver) this.headerResizeObserver.disconnect();
      window.removeEventListener('scroll', this.onScrollEvent);
      if (this.placeDrop) window.removeEventListener('resize', this.placeDrop);
      window.removeEventListener('pageshow', this.onPageShow);
      document.documentElement.classList.remove('cc-lock');
    }

    readJSON(node) {
      try {
        return node ? JSON.parse(node.textContent) : {};
      } catch (error) {
        return {};
      }
    }

    plural(forms, count) {
      if (!forms || typeof forms === 'string') return forms || '';
      return forms[this.plurals.select(count)] || forms.other || '';
    }

    announce(text) {
      if (!this.live) return;
      window.clearTimeout(this.announceTimer);
      this.live.textContent = '';
      this.announceTimer = window.setTimeout(() => {
        this.live.textContent = text;
      }, 80);
    }

    /* Shades, filters, search and sorting */

    setupChart() {
      this.tiles = [...this.grid.querySelectorAll('[data-shade]')];
      this.shades = this.tiles.map((tile) => this.readShade(tile));
      this.order = [...this.shades];
      this.byHandle = new Map(this.shades.map((shade) => [shade.handle, shade]));
      this.byVariant = new Map(this.shades.map((shade) => [shade.variant, shade]));
      this.hasOfferShades = this.shades.some((shade) => shade.offer);
      this.divider = this.grid.querySelector('[data-divider]');
      this.dividerLabel = this.divider && this.divider.querySelector('[data-divider-label]');
      this.labels = this.readLabels();
      // How people spell the Latin finish names in this language ("γκλιτερ" finds Glitter)
      const aliases = new Map(
        String(this.i18n.aliases || '')
          .split('|')
          .map((pair) => pair.split('='))
          .filter((pair) => pair.length === 2 && pair[0].trim())
          .map(([key, value]) => [key.trim(), value])
      );
      this.shades.forEach((shade) => {
        const extra = [
          ...shade.finish.map((value) => this.labels.finish.get(value) || value),
          ...shade.finish.map((value) => aliases.get(value) || ''),
          ...shade.occasion.map((value) => this.labels.occasion.get(value) || value),
        ];
        shade.search = fold(`${shade.searchBase} ${extra.join(' ')}`);
      });
      this.setupFamilies();

      this.browse = this.querySelector('.cc-browse');
      this.toolbar = this.querySelector('[data-toolbar]');
      this.quick = this.querySelector('[data-quick]');
      this.search = this.querySelector('[data-search]');
      this.sortSelect = this.querySelector('[data-sort]');
      this.sheet = this.querySelector('[data-sheet]');
      this.activeWrap = this.querySelector('[data-active]');
      this.activeList = this.querySelector('[data-active-list]');
      this.empty = this.querySelector('[data-empty]');
      this.emptyTitle = this.querySelector('[data-empty-title]');
      this.emptyHint = this.querySelector('[data-empty-hint]');
      this.emptyAction = this.querySelector('[data-empty-action]');
      this.missingNode = this.querySelector('[data-missing]');
      this.activeClear = this.querySelector('[data-active-clear]');
      this.allSold = this.querySelector('[data-all-sold]');
      this.searchClear = this.querySelector('[data-search-clear]');
      this.countChips = [...this.querySelectorAll('[data-sheet] [data-filter], [data-drop-panel] [data-filter]')];
      this.closeIcon = this.querySelector('[data-close-sheet] .cc-icon');
      const saleChip = this.querySelector('[data-filter="sale"] .cc-chip__label');
      this.saleLabel = saleChip ? saleChip.textContent.trim() : this.i18n.offersOnly;
      this.setupDrops();

      this.defaults = { avail: this.soldOutMode === 'hidden', sort: 'number' };
      this.state = this.readURL();

      if (this.search) {
        this.search.value = this.state.q;
        // One update per frame however fast the typing: re-showing all 234 shades costs a layout pass
        this.search.addEventListener('input', () => {
          this.state.q = this.search.value;
          if (this.searchFrame) return;
          this.searchFrame = window.requestAnimationFrame(() => {
            this.searchFrame = null;
            this.update({ announce: true, lazyURL: true });
          });
        });
        if (this.searchClear) {
          this.searchClear.addEventListener('click', () => {
            this.search.value = '';
            this.state.q = '';
            this.update({ announce: true });
            this.search.focus();
          });
        }
        this.search.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.search.blur();
            this.scrollToResults();
          }
        });
      }
      if (this.sortSelect) {
        this.sortSelect.value = this.state.sort;
        this.sortSelect.addEventListener('change', () => this.setSort(this.sortSelect.value));
      }
      if (this.sheet) {
        this.sheet.addEventListener('click', (event) => {
          if (event.target === this.sheet) this.closeSheet();
        });
        this.sheet.addEventListener('close', () => this.onSheetClosed());
      }

      // Load a shade's product data once the pointer rests on it, so its panel opens complete.
      const prefetch = (event, delay) => {
        const link = event.target.closest('[data-open]');
        if (!link || link === this.prefetchLink) return;
        this.prefetchLink = link;
        window.clearTimeout(this.prefetchTimer);
        this.prefetchTimer = window.setTimeout(() => this.productData(this.shadeOf(link)), delay);
      };
      const cancelPrefetch = (event) => {
        const link = event.target.closest('[data-open]');
        if (link && !link.contains(event.relatedTarget)) {
          window.clearTimeout(this.prefetchTimer);
          this.prefetchLink = null;
        }
      };
      this.grid.addEventListener('pointerover', (event) => prefetch(event, 180));
      this.grid.addEventListener('pointerout', cancelPrefetch);
      this.grid.addEventListener('focusin', (event) => prefetch(event, 400));
      this.grid.addEventListener('focusout', cancelPrefetch);
      this.grid.addEventListener(
        'pointerdown',
        (event) => {
          const link = event.target.closest('[data-open]');
          if (link) this.productData(this.shadeOf(link));
        },
        { passive: true }
      );
      // Photographed tips (about 220 photos) load as their tiles come near the screen; until then
      // the tile shows the shade's colour (snippets/onyx-nail-tip.liquid, lazy: true)
      const lazyTips = this.querySelectorAll('[data-tip-lazy]');
      if ('IntersectionObserver' in window) {
        this.tipObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.removeAttribute('data-tip-lazy');
              this.tipObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '600px 0px' }
        );
        lazyTips.forEach((nail) => this.tipObserver.observe(nail));
      } else {
        lazyTips.forEach((nail) => nail.removeAttribute('data-tip-lazy'));
      }

      // Mouse: the with-flash photos load while the page is idle, so a hover's burst shows them
      if (window.matchMedia('(hover: hover)').matches) {
        const preload = () => this.querySelectorAll('.cc-tip__nail--has-flash').forEach((nail) => this.loadFlash(nail.closest('.cc-tip')));
        if ('requestIdleCallback' in window) window.requestIdleCallback(preload, { timeout: 4000 });
        else window.setTimeout(preload, 2500);
      }

      this.panelTemplate = this.querySelector('[data-panel-template]');
      this.resizeObserver = new ResizeObserver(() => {
        if (this.openShade && this.panel && this.panel.isConnected) this.placePanel();
      });
      this.resizeObserver.observe(this.grid);

      this.applySort();
      this.update({ initial: true });

      const handle = new URLSearchParams(window.location.search).get('shade');
      if (handle) {
        window.requestAnimationFrame(() => {
          const shade = this.byHandle.get(handle);
          if (shade && !shade.el.hidden) this.open(shade, { reveal: 'center', focus: false });
        });
      }
    }

    readShade(tile) {
      const data = tile.dataset;
      const list = (value) => String(value || '').split('|').filter(Boolean);
      const digits = String(data.number || '').match(/\d+/);
      return {
        el: tile,
        link: tile.querySelector('[data-open]'),
        addButton: tile.querySelector('[data-add]'),
        subButton: tile.querySelector('[data-sub]'),
        img: tile.querySelector('.cc-tile__img'),
        // Nail-tip mode (section setting): the drawn tip, reused in the panel and the similar shades
        tip: tile.querySelector('.cc-tip'),
        handle: data.handle,
        url: data.url,
        variant: Number(data.variant),
        // How many the cart may hold. Unknown (Infinity) until Shopify refuses one more; the page never
        // carries inventory numbers.
        stock: Infinity,
        available: data.available === 'true',
        price: Number(data.price) || 0,
        compare: Number(data.compare) || 0,
        percent: Number(data.percent) || 0,
        family: data.family || '',
        finish: list(data.finish),
        occasion: list(data.occasion),
        coverage: data.coverage || '',
        sale: data.sale === 'true',
        isNew: data.new === 'true',
        offer: data.offer === 'true',
        hema: data.hema === 'true',
        number: data.number || '',
        numberKey: withoutLeadingZeros(fold(data.number || '')),
        numberValue: digits ? Number(digits[0]) : null,
        name: data.name || '',
        title: [data.number, data.name].filter(Boolean).join(' '),
        searchBase: data.search || '',
        hex: data.hex || '',
        lab: hexToLab(data.hex),
        image: data.image || '',
        // finish group for "similar shades": cat eye, sparkle (flash, glitter, metallic, pearl, shimmer) or plain
        effect: (() => {
          const tip = tile.querySelector('.cc-tip');
          const effect = tip ? tip.dataset.effect || '' : '';
          if (effect === 'cat-eye') return 'cat';
          return effect ? 'sparkle' : 'plain';
        })(),
        index: Number(data.index) || 0,
      };
    }

    // Each colour family's chips get the average measured colour of its shades as their dot, and follow
    // the colour wheel (as the "by colour" sort does) from pink and red through yellow, green and blue to
    // purple, then the neutrals from white to black. The section renders them as first met.
    setupFamilies() {
      const sums = new Map();
      this.shades.forEach((shade) => {
        const match = /^#?([0-9a-f]{6})$/i.exec(String(shade.hex).trim());
        if (!shade.family || !match) return;
        const value = parseInt(match[1], 16);
        const sum = sums.get(shade.family) || [0, 0, 0, 0];
        sum[0] += (value >> 16) & 255;
        sum[1] += (value >> 8) & 255;
        sum[2] += value & 255;
        sum[3] += 1;
        sums.set(shade.family, sum);
      });
      const families = new Map();
      sums.forEach(([r, g, b, count], family) => {
        const hex = `#${[r, g, b].map((channel) => Math.round(channel / count).toString(16).padStart(2, '0')).join('')}`;
        const [L, A, B] = hexToLab(hex);
        const angle = ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360;
        const key = Math.hypot(A, B) < 10 ? 1000 + (100 - L) : (angle + 30) % 360;
        families.set(family, { hex, key });
      });
      const keyOf = (chip) => (families.has(chip.dataset.value) ? families.get(chip.dataset.value).key : 5000);
      this.querySelectorAll('[data-filter="family"]').forEach((chip) => {
        const dot = chip.querySelector('.cc-chip__dot');
        const family = families.get(chip.dataset.value);
        if (dot && family) dot.style.setProperty('--dot', family.hex);
      });
      new Set([...this.querySelectorAll('[data-filter="family"]')].map((chip) => chip.parentElement)).forEach((group) => {
        const chips = [...group.children].filter((child) => child.matches('[data-filter="family"]'));
        chips.sort((a, b) => keyOf(a) - keyOf(b)).forEach((chip) => group.append(chip));
      });
    }

    readLabels() {
      const labels = { family: new Map(), finish: new Map(), occasion: new Map() };
      this.querySelectorAll('[data-filter][data-value]').forEach((chip) => {
        const group = labels[chip.dataset.filter];
        const label = chip.querySelector('.cc-chip__label');
        if (group && label && !group.has(chip.dataset.value)) group.set(chip.dataset.value, label.textContent.trim());
      });
      return labels;
    }

    shadeOf(node) {
      const tile = node && node.closest('[data-shade]');
      return tile ? this.byHandle.get(tile.dataset.handle) : null;
    }

    readURL() {
      const params = new URLSearchParams(window.location.search);
      const values = (key, group) =>
        new Set(
          String(params.get(key) || '')
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value && this.labels[group].has(value))
        );
      const sort = params.get('sort');
      return {
        q: params.get('q') || '',
        family: values(GROUP_PARAMS.family, 'family'),
        finish: values(GROUP_PARAMS.finish, 'finish'),
        occasion: values(GROUP_PARAMS.occasion, 'occasion'),
        sale: params.get('sale') === '1',
        avail: params.has('avail') ? params.get('avail') === '1' : this.defaults.avail,
        sort: SORTS.includes(sort) ? sort : this.defaults.sort,
      };
    }

    writeURL() {
      if (!this.state) return;
      const url = new URL(window.location.href);
      const params = url.searchParams;
      const set = (key, value) => (value ? params.set(key, value) : params.delete(key));
      set('q', this.state.q.trim());
      GROUPS.forEach((group) => set(GROUP_PARAMS[group], [...this.state[group]].join(',')));
      set('sale', this.state.sale ? '1' : '');
      set('avail', this.state.avail === this.defaults.avail ? '' : this.state.avail ? '1' : '0');
      set('sort', this.state.sort === this.defaults.sort ? '' : this.state.sort);
      set('shade', this.openShade ? this.openShade.handle : '');
      params.delete('customer_posted');
      if (url.hash.startsWith('#CcNotify-')) url.hash = '';
      const next = url.pathname + url.search + url.hash;
      if (next !== window.location.pathname + window.location.search + window.location.hash) {
        window.history.replaceState(window.history.state, '', next);
      }
    }

    hasFilters() {
      const state = this.state;
      return Boolean(state.q.trim() || state.sale || state.avail || GROUPS.some((group) => state[group].size));
    }

    filterCount() {
      const state = this.state;
      return (state.sale ? 1 : 0) + (state.avail ? 1 : 0) + GROUPS.reduce((sum, group) => sum + state[group].size, 0);
    }

    matches(shade, except) {
      const state = this.state;
      if (except !== 'avail' && state.avail && !shade.available) return false;
      if (except !== 'sale' && state.sale && !shade.sale) return false;
      if (except !== 'family' && state.family.size && !state.family.has(shade.family)) return false;
      if (except !== 'finish' && state.finish.size && !shade.finish.some((value) => state.finish.has(value))) return false;
      if (except !== 'occasion' && state.occasion.size && !shade.occasion.some((value) => state.occasion.has(value))) {
        return false;
      }
      if (this.numberQuery) return this.tokens.some((token) => this.matchesToken(shade, token));
      return this.tokens.every((token) => this.matchesToken(shade, token));
    }

    // A token of digits is a whole shade number: "17" and "017" find 017 only. A query of numbers
    // alone ("17 29 186", "17, 29") lists every shade it names; words still narrow each other.
    matchesToken(shade, token) {
      if (/^\d+$/.test(token)) return shade.numberKey === withoutLeadingZeros(token);
      return shade.search.includes(token);
    }

    // Typed numbers that no shade in the chart has, shown under the search so a list can be checked.
    renderMissing() {
      if (!this.missingNode) return;
      const missing = this.numberQuery
        ? this.tokens.filter((token) => !this.shades.some((shade) => shade.numberKey === withoutLeadingZeros(token)))
        : [];
      const numbers = missing.length > 6 ? `${missing.slice(0, 6).join(', ')}…` : missing.join(', ');
      const text = missing.length ? fill(this.i18n.notFound, { numbers }) : '';
      if (this.missingNode.textContent !== text) this.missingNode.textContent = text;
      this.missingNode.hidden = !text;
    }

    update({ announce = false, initial = false, lazyURL = false } = {}) {
      // "0" or "00" on the way to "017" narrows nothing yet (instead of showing no shades)
      this.tokens = [...new Set(fold(this.state.q).split(/[\s,;+#/.\-]+/).filter((token) => token && !/^0+$/.test(token)))];
      this.numberQuery = this.tokens.length > 0 && this.tokens.every((token) => /^\d+$/.test(token));
      let shown = 0;
      let soldShown = 0;
      this.shades.forEach((shade) => {
        const visible = this.matches(shade);
        if (shade.el.hidden === visible) shade.el.hidden = !visible;
        if (visible) {
          shown += 1;
          if (!shade.available) soldShown += 1;
        }
      });
      this.shown = shown;
      if (this.divider) {
        const showDivider = this.soldOutMode !== 'mixed' && soldShown > 0;
        if (this.divider.hidden === showDivider) this.divider.hidden = !showDivider;
        if (showDivider && this.dividerLabel) this.dividerLabel.textContent = fill(this.i18n.soldOutHeading, { count: soldShown });
      }

      if (this.openShade) {
        if (this.openShade.el.hidden) this.close({ focus: false, silent: true });
        else this.placePanel();
      }

      this.renderChips();
      this.renderActive();
      this.renderMissing();
      this.renderActiveRow();
      this.renderCounts();
      if (this.searchClear) this.searchClear.hidden = !this.state.q;
      if (this.allSold) this.allSold.hidden = !(shown > 0 && soldShown === shown);
      if (this.empty) {
        this.empty.hidden = shown > 0;
        if (!shown) this.renderEmpty();
      }
      if ((this.sheet && this.sheet.open) || this.openDrop) this.renderChipCounts();

      if (!initial) {
        window.clearTimeout(this.urlTimer);
        if (lazyURL) this.urlTimer = window.setTimeout(() => this.writeURL(), 400);
        else this.writeURL();
      }
      if (announce) {
        window.clearTimeout(this.countTimer);
        this.countTimer = window.setTimeout(() => {
          const note = this.missingNode && !this.missingNode.hidden ? this.missingNode.textContent : '';
          this.announce(note ? `${this.countText()}. ${note}` : this.countText());
        }, 600);
      }
    }

    countText() {
      const total = this.shades.length;
      if (this.shown === total && !this.hasFilters()) return fill(this.plural(this.i18n.count, total), { count: total });
      return fill(this.i18n.shown, { shown: this.shown, total });
    }

    renderCounts() {
      const text = this.countText();
      this.querySelectorAll('[data-count]').forEach((node) => {
        if (node.textContent !== text) node.textContent = text;
      });
      const apply = this.querySelector('[data-apply-label]');
      if (apply) apply.textContent = fill(this.plural(this.i18n.showResults, this.shown), { count: this.shown });
      const badge = this.querySelector('[data-filter-count]');
      if (badge) {
        const count = this.filterCount();
        badge.hidden = count === 0;
        badge.textContent = count;
      }
      this.querySelectorAll('[data-drop-count]').forEach((dropBadge) => {
        const count = this.state[dropBadge.dataset.dropCount] ? this.state[dropBadge.dataset.dropCount].size : 0;
        dropBadge.hidden = count === 0;
        dropBadge.textContent = count;
      });
    }

    renderChips() {
      this.querySelectorAll('[data-filter]').forEach((chip) => {
        const { filter, value } = chip.dataset;
        let on = false;
        if (filter === 'sale') on = this.state.sale;
        else if (filter === 'avail') on = this.state.avail;
        else if (this.state[filter]) on = this.state[filter].has(value);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.querySelectorAll('[data-sort-chip]').forEach((chip) => {
        chip.setAttribute('aria-pressed', chip.dataset.sortChip === this.state.sort ? 'true' : 'false');
      });
    }

    // Each filter option shows how many shades it would give with the other filters as they are.
    renderChipCounts() {
      this.countChips.forEach((chip) => {
        const { filter, value } = chip.dataset;
        let count = 0;
        this.shades.forEach((shade) => {
          if (!this.matches(shade, filter)) return;
          if (filter === 'sale' ? shade.sale : filter === 'avail' ? shade.available : filter === 'family' ? shade.family === value : shade[filter].includes(value)) {
            count += 1;
          }
        });
        const output = chip.querySelector('[data-chip-count]');
        if (output) output.textContent = count;
        chip.classList.toggle('is-empty', count === 0 && chip.getAttribute('aria-pressed') !== 'true');
      });
    }

    renderActive() {
      if (!this.activeList) return;
      const pills = [];
      if (this.state.sale) pills.push({ key: 'sale', label: this.saleLabel });
      if (this.state.avail) pills.push({ key: 'avail', label: this.i18n.inStockOnly });
      GROUPS.forEach((group) => {
        this.state[group].forEach((value) => {
          pills.push({ key: `${group}:${value}`, label: this.labels[group].get(value) || value });
        });
      });
      this.activePills = pills.length;
      const signature = pills.map((pill) => `${pill.key}=${pill.label}`).join('|');
      if (signature === this.activeSignature) return;
      this.activeSignature = signature;
      this.activeList.replaceChildren(
        ...pills.map((pill) => {
          const item = element('li');
          const button = element('button', 'cc-pill');
          button.type = 'button';
          button.dataset.pill = pill.key;
          button.setAttribute('aria-label', fill(this.i18n.remove, { label: pill.label }));
          button.append(element('span', '', pill.label));
          if (this.closeIcon) button.append(this.closeIcon.cloneNode(true));
          item.append(button);
          return item;
        })
      );
    }

    // The row shows while there is a filter pill or a not-found line; "clear all" only with pills.
    renderActiveRow() {
      if (!this.activeWrap) return;
      const pills = this.activePills || 0;
      const note = Boolean(this.missingNode && !this.missingNode.hidden);
      this.activeWrap.hidden = pills === 0 && !note;
      if (this.activeClear) this.activeClear.hidden = pills === 0;
    }

    // A search alone that finds nothing names the search and shows an example of what works.
    renderEmpty() {
      if (!this.emptyDefaults) {
        this.emptyDefaults = {
          title: this.emptyTitle ? this.emptyTitle.textContent : '',
          action: this.emptyAction ? this.emptyAction.textContent : '',
        };
      }
      const query = this.state.q.trim();
      const searchOnly = Boolean(query) && this.filterCount() === 0;
      if (this.emptyTitle) {
        this.emptyTitle.textContent = searchOnly ? fill(this.i18n.emptySearch, { query }) : this.emptyDefaults.title;
      }
      if (this.emptyHint) this.emptyHint.hidden = !searchOnly || this.numberQuery;
      if (this.emptyAction) this.emptyAction.textContent = searchOnly ? this.i18n.clearSearch || this.emptyDefaults.action : this.emptyDefaults.action;
    }

    removePill(button) {
      const key = button.dataset.pill;
      const buttons = [...this.activeList.querySelectorAll('[data-pill]')];
      const index = buttons.indexOf(button);
      if (key === 'q') {
        this.state.q = '';
        if (this.search) this.search.value = '';
      } else if (key === 'sale') {
        this.state.sale = false;
      } else if (key === 'avail') {
        this.state.avail = false;
      } else {
        const [group, ...rest] = key.split(':');
        if (this.state[group]) this.state[group].delete(rest.join(':'));
      }
      this.update({ announce: true });
      const remaining = [...this.activeList.querySelectorAll('[data-pill]')];
      const next = remaining[Math.min(index, remaining.length - 1)];
      if (next) next.focus();
      else if (this.search) this.search.focus({ preventScroll: true });
    }

    toggleFilter(chip) {
      const { filter, value } = chip.dataset;
      if (filter === 'sale') this.state.sale = !this.state.sale;
      else if (filter === 'avail') this.state.avail = !this.state.avail;
      else if (this.state[filter]) {
        if (this.state[filter].has(value)) this.state[filter].delete(value);
        else this.state[filter].add(value);
      }
      this.update({ announce: true });
      if (!chip.closest('[data-sheet]')) this.scrollToResults();
    }

    clearFilters() {
      this.state.q = '';
      if (this.search) this.search.value = '';
      this.state.sale = false;
      this.state.avail = false;
      GROUPS.forEach((group) => this.state[group].clear());
      this.update({ announce: true });
    }

    setSort(mode) {
      this.state.sort = SORTS.includes(mode) ? mode : this.defaults.sort;
      if (this.sortSelect) this.sortSelect.value = this.state.sort;
      this.applySort();
      this.update();
      this.scrollToResults();
    }

    hueKey(shade) {
      if (shade.hueKey !== undefined) return shade.hueKey;
      if (!shade.lab) {
        shade.hueKey = 1e7;
        return shade.hueKey;
      }
      const [L, a, b] = shade.lab;
      const chroma = Math.hypot(a, b);
      if (chroma < 8) {
        shade.hueKey = 1e6 + (100 - L);
      } else {
        // Colour-wheel bands from magenta and pink through red, yellow, green and blue; light to dark within a band.
        const angle = (Math.atan2(b, a) * 180) / Math.PI;
        const band = Math.floor((((angle + 360) % 360) + 30) % 360 / 20);
        shade.hueKey = band * 1000 + (100 - L);
      }
      return shade.hueKey;
    }

    compareShades(a, b, mode) {
      const lightness = (shade) => (shade.lab ? shade.lab[0] : null);
      const nullsLast = (x, y, direction) => {
        if (x === null && y === null) return 0;
        if (x === null) return 1;
        if (y === null) return -1;
        return direction * (x - y);
      };
      switch (mode) {
        case 'light':
          return nullsLast(lightness(a), lightness(b), -1);
        case 'dark':
          return nullsLast(lightness(a), lightness(b), 1);
        case 'hue':
          return this.hueKey(a) - this.hueKey(b);
        case 'newest':
          return Number(b.isNew) - Number(a.isNew) || nullsLast(a.numberValue, b.numberValue, -1);
        default:
          if (!a.number || !b.number) return (a.number ? 0 : 1) - (b.number ? 0 : 1);
          return this.collator.compare(a.number, b.number);
      }
    }

    applySort() {
      const mode = this.state.sort;
      const availableFirst = this.soldOutMode !== 'mixed';
      const sorted = [...this.shades].sort((a, b) => {
        if (availableFirst && a.available !== b.available) return a.available ? -1 : 1;
        return this.compareShades(a, b, mode) || a.index - b.index;
      });
      if (sorted.every((shade, index) => this.order[index] === shade)) return;
      const openShade = this.openShade;
      if (openShade) this.close({ focus: false, silent: true });
      this.order = sorted;
      this.grid.append(...sorted.map((shade) => shade.el));
      if (this.divider) {
        const firstSoldOut = availableFirst ? sorted.find((shade) => !shade.available) : null;
        if (firstSoldOut) firstSoldOut.el.before(this.divider);
        else this.grid.append(this.divider);
      }
      if (openShade && !openShade.el.hidden) this.open(openShade, { reveal: false, focus: false });
    }

    stickyBottom() {
      const top = this.stickyTop || 0;
      return top + (this.toolbar ? this.toolbar.offsetHeight : 0);
    }

    // After a filter or sort change, bring the start of the results into view if the visitor had scrolled past it.
    scrollToResults() {
      // the quick chips, else the active-filter pills, else the grid: the first one on screen
      const target = [this.quick, this.activeWrap, this.grid].find((el) => el && el.getClientRects().length > 0);
      if (!target) return;
      const offset = this.stickyBottom() + 8;
      const top = target.getBoundingClientRect().top;
      if (top < offset) {
        window.scrollBy({ top: top - offset, behavior: scrollBehaviour() });
      }
    }

    /* Finish and occasion dropdowns (desktop toolbar), native popovers placed under their buttons */

    setupDrops() {
      const panels = [...this.querySelectorAll('[data-drop-panel]')];
      if (!panels.length || typeof HTMLElement.prototype.togglePopover !== 'function') return;
      const place = (panel) => {
        const button = this.querySelector(`[popovertarget="${panel.id}"]`);
        if (!button) return;
        const rect = button.getBoundingClientRect();
        const width = Math.min(360, window.innerWidth - 24);
        const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
        panel.style.setProperty('--drop-top', `${Math.round(rect.bottom + 8)}px`);
        panel.style.setProperty('--drop-left', `${Math.round(left)}px`);
      };
      panels.forEach((panel) => {
        panel.addEventListener('beforetoggle', (event) => {
          if (event.newState === 'open') {
            place(panel);
            this.openDrop = panel;
            this.renderChipCounts();
          }
        });
        panel.addEventListener('toggle', (event) => {
          const open = event.newState === 'open';
          const button = this.querySelector(`[popovertarget="${panel.id}"]`);
          if (button) button.setAttribute('aria-expanded', open ? 'true' : 'false');
          if (!open && this.openDrop === panel) this.openDrop = null;
        });
      });
      this.placeDrop = () => {
        if (this.openDrop) place(this.openDrop);
      };
      window.addEventListener('resize', this.placeDrop);
    }

    /* Filter sheet */

    openSheet() {
      if (!this.sheet || this.sheet.open) return;
      if (this.openDrop && typeof this.openDrop.hidePopover === 'function') this.openDrop.hidePopover();
      this.sheetOpener = document.activeElement;
      this.sheetState = this.stateSignature();
      this.renderChipCounts();
      this.sheet.showModal();
      document.documentElement.classList.add('cc-lock');
    }

    closeSheet() {
      if (this.sheet && this.sheet.open) this.sheet.close();
    }

    onSheetClosed() {
      document.documentElement.classList.remove('cc-lock');
      if (this.sheetOpener && document.contains(this.sheetOpener)) this.sheetOpener.focus({ preventScroll: true });
      if (this.sheetState !== this.stateSignature()) this.scrollToResults();
    }

    stateSignature() {
      const state = this.state;
      return JSON.stringify([state.q, state.sale, state.avail, state.sort, ...GROUPS.map((group) => [...state[group]])]);
    }

    /* Shade panel */

    createPanel() {
      const panel = this.panelTemplate.content.firstElementChild.cloneNode(true);
      panel.id = `CcPanel-${this.dataset.sectionId}`;
      this.panelParts = {
        card: panel.querySelector('.cc-panel__card'),
        notch: panel.querySelector('[data-panel-notch]'),
        swatch: panel.querySelector('[data-panel-swatch]'),
        flash: panel.querySelector('[data-panel-flash]'),
        bottle: panel.querySelector('[data-panel-bottle]'),
        title: panel.querySelector('[data-panel-title]'),
        facts: panel.querySelector('[data-panel-facts]'),
        price: panel.querySelector('[data-panel-price]'),
        desc: panel.querySelector('[data-panel-desc]'),
        buy: panel.querySelector('[data-panel-buy]'),
        add: panel.querySelector('[data-panel-add]'),
        addLabel: panel.querySelector('[data-panel-add-label]'),
        inCart: panel.querySelector('[data-panel-in-cart]'),
        stepper: panel.querySelector('[data-panel-stepper]'),
        qty: panel.querySelector('[data-panel-qty]'),
        plus: panel.querySelector('[data-panel-plus]'),
        notify: panel.querySelector('[data-panel-notify]'),
        notifyMessage: panel.querySelector('[data-panel-notify-message]'),
        link: panel.querySelector('[data-panel-link]'),
        prev: panel.querySelector('[data-panel-prev]'),
        next: panel.querySelector('[data-panel-next]'),
        similar: panel.querySelector('[data-panel-similar]'),
        similarTitle: panel.querySelector('[data-panel-similar-title]'),
        similarList: panel.querySelector('[data-panel-similar-list]'),
      };
      return panel;
    }

    visibleShades() {
      return this.order.filter((shade) => !shade.el.hidden);
    }

    // The panel is a full-width grid row placed after the last tile of the open shade's row: the run of
    // visible tiles sharing its top edge (a full-width divider starts a new row, so counting columns would not do).
    placePanel() {
      const shade = this.openShade;
      if (!shade || shade.el.hidden) return false;
      const top = shade.el.offsetTop;
      let anchor = shade.el;
      for (let node = shade.el.nextElementSibling; node; node = node.nextElementSibling) {
        if (node === this.panel || node.hidden) continue;
        if (!node.hasAttribute('data-shade') || node.offsetTop !== top) break;
        anchor = node;
      }
      let moved = false;
      if (this.panel.previousElementSibling !== anchor || !this.panel.isConnected) {
        const focused = this.panel.contains(document.activeElement) ? document.activeElement : null;
        anchor.after(this.panel);
        if (focused) focused.focus({ preventScroll: true });
        moved = true;
      }
      const swatch = shade.el.querySelector('.cc-tile__swatch') || shade.el;
      const swatchRect = swatch.getBoundingClientRect();
      const cardRect = this.panelParts.card.getBoundingClientRect();
      const x = Math.min(Math.max(swatchRect.left + swatchRect.width / 2 - cardRect.left, 24), cardRect.width - 24);
      this.panelParts.card.style.setProperty('--notch-x', `${x}px`);
      return moved;
    }

    open(shade, { reveal = 'nearest', focus = true, anchor = false, notify = false } = {}) {
      if (!shade || shade.el.hidden) return;
      if (!this.panel) this.panel = this.createPanel();
      const previous = this.openShade;
      const anchorTop = anchor ? shade.el.getBoundingClientRect().top : 0;

      if (previous && previous !== shade) {
        previous.el.classList.remove('is-open');
        if (previous.link) {
          previous.link.setAttribute('aria-expanded', 'false');
          previous.link.removeAttribute('aria-controls');
        }
      }
      this.openShade = shade;
      shade.el.classList.add('is-open');
      if (shade.link) {
        shade.link.setAttribute('aria-expanded', 'true');
        shade.link.setAttribute('aria-controls', this.panel.id);
      }

      this.fillPanel(shade);
      this.placePanel();

      if (anchor) {
        // Keep the tapped tile where it was on screen when the panel leaves a row above it.
        const delta = shade.el.getBoundingClientRect().top - anchorTop;
        if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: 'instant' });
      }
      if (reveal) this.revealPanel(reveal);
      if (notify && !shade.available) {
        // On touch screens focusing the field would raise the keyboard over the alternatives
        const input = this.panel.querySelector('.cc-notify__input');
        const coarse = window.matchMedia('(pointer: coarse)').matches;
        if (input && !coarse && !this.notified.has(shade.handle)) input.focus({ preventScroll: true });
        else this.panelParts.title.focus({ preventScroll: true });
      } else if (focus) {
        this.panelParts.title.focus({ preventScroll: true });
      }
      this.writeURL();
    }

    revealPanel(mode) {
      const shadeRect = this.openShade.el.getBoundingClientRect();
      const panelRect = this.panel.getBoundingClientRect();
      const top = this.stickyBottom() + 12;
      const bottom = window.innerHeight - 12;
      let delta = 0;
      if (mode === 'center') {
        delta = shadeRect.top - top - 24;
      } else if (shadeRect.top < top) {
        delta = shadeRect.top - top;
      } else if (panelRect.bottom > bottom) {
        delta = Math.min(panelRect.bottom - bottom, shadeRect.top - top);
      }
      if (Math.abs(delta) > 1) window.scrollBy({ top: delta, behavior: scrollBehaviour() });
    }

    close({ focus = true, silent = false } = {}) {
      const shade = this.openShade;
      if (!shade) return;
      const hadFocus = this.panel && this.panel.contains(document.activeElement);
      if (this.panel) this.panel.remove();
      shade.el.classList.remove('is-open');
      if (shade.link) {
        shade.link.setAttribute('aria-expanded', 'false');
        shade.link.removeAttribute('aria-controls');
      }
      this.openShade = null;
      if (focus && hadFocus && shade.link && !shade.el.hidden) shade.link.focus({ preventScroll: true });
      if (!silent) this.writeURL();
    }

    step(direction) {
      if (!this.openShade) return;
      const visible = this.visibleShades();
      const next = visible[visible.indexOf(this.openShade) + direction];
      if (!next) return;
      const button = direction < 0 ? this.panelParts.prev : this.panelParts.next;
      this.open(next, { anchor: true, reveal: 'nearest', focus: false });
      if (button && !button.disabled) button.focus({ preventScroll: true });
      else this.panelParts.title.focus({ preventScroll: true });
    }

    priceNodes(shade) {
      const nodes = [];
      if (!shade.available) {
        nodes.push(element('span', 'cc-panel__status', this.i18n.soldOut));
        return nodes;
      }
      if (shade.sale) {
        nodes.push(element('span', 'cc-price cc-price--sale', this.money(shade.price)));
        const was = element('s', 'cc-price cc-price--was');
        was.append(element('span', 'visually-hidden', `${this.i18n.was} `), this.money(shade.compare));
        nodes.push(was);
        if (shade.percent) nodes.push(element('span', 'cc-badge cc-badge--sale', `−${shade.percent}%`));
      } else {
        nodes.push(element('span', 'cc-price', this.money(shade.price)));
      }
      return nodes;
    }

    money(cents) {
      if (window.theme && theme.Currency && theme.shopSettings && theme.shopSettings.moneyFormat) {
        return theme.Currency.formatMoney(cents, theme.shopSettings.moneyFormat).replace(/<[^>]*>/g, '');
      }
      return (cents / 100).toLocaleString(this.lang, { style: 'currency', currency: 'EUR' });
    }

    fillPanel(shade) {
      const parts = this.panelParts;
      parts.card.setAttribute('aria-label', fill(this.i18n.panelLabel, { title: shade.title }));

      parts.title.replaceChildren();
      if (shade.number) parts.title.append(element('span', 'cc-panel__num', shade.number), ' ');
      parts.title.append(shade.name);

      parts.swatch.style.setProperty('--panel-hex', shade.hex || 'transparent');
      parts.swatch.replaceChildren();
      const src = shade.img && shade.img.getAttribute('src');
      if (shade.tip) {
        parts.swatch.append(this.copyTip(shade.tip));
      } else if (src) {
        const image = element('img');
        image.alt = '';
        image.width = 400;
        image.height = 400;
        image.src = resizeImage(src, 400);
        parts.swatch.append(image);
      }
      if (parts.flash) {
        const hasFlash = Boolean(shade.tip && shade.tip.querySelector('.cc-tip__flash'));
        parts.flash.hidden = !hasFlash;
        parts.flash.setAttribute('aria-pressed', 'false');
        if (hasFlash) this.loadFlash(shade.tip);
      }

      const facts = [];
      if (shade.family) facts.push(this.labels.family.get(shade.family));
      shade.finish.forEach((value) => facts.push(this.labels.finish.get(value) || value));
      if (shade.coverage) facts.push(fill(this.i18n.coverage, { value: shade.coverage }));
      if (shade.hema) facts.push(this.i18n.hemaFree);
      parts.facts.replaceChildren(...facts.filter(Boolean).map((fact) => element('li', '', fact)));
      parts.facts.hidden = facts.length === 0;

      parts.price.replaceChildren(...this.priceNodes(shade));
      parts.link.href = shade.url;

      parts.buy.hidden = !shade.available;
      this.renderPanelCart();

      const canNotify = !shade.available && Boolean(this.notifyForm);
      parts.notify.hidden = !canNotify;
      parts.notify.classList.toggle('is-done', this.notified.has(shade.handle));
      parts.notifyMessage.textContent = this.notified.has(shade.handle) ? this.i18n.notifyDone : '';
      const input = parts.notify.querySelector('.cc-notify__input');
      if (input) input.removeAttribute('aria-invalid');

      const visible = this.visibleShades();
      const position = visible.indexOf(shade);
      parts.prev.disabled = position <= 0;
      parts.next.disabled = position >= visible.length - 1;

      this.renderSimilar(shade);
      // Sold out on a narrow screen: the in-stock alternatives come before the restock form
      const narrow = window.matchMedia('(max-width: 989px)').matches;
      if (!shade.available && narrow && !parts.similar.hidden) parts.notify.before(parts.similar);
      else if (parts.similar.parentElement !== parts.card) parts.card.append(parts.similar);

      parts.desc.textContent = '';
      parts.bottle.hidden = false;
      // The tile carries its photo's address, so the bottle shows with the panel; the description follows
      if (shade.image) {
        parts.bottle.classList.remove('is-loading');
        parts.bottle.replaceChildren(this.bottleImage(shade.image));
      } else {
        parts.bottle.replaceChildren();
        parts.bottle.classList.add('is-loading');
      }
      this.productData(shade).then((product) => {
        if (this.openShade !== shade) return;
        parts.bottle.classList.remove('is-loading');
        if (!shade.image) {
          if (product && product.featured_image) parts.bottle.replaceChildren(this.bottleImage(String(product.featured_image)));
          else parts.bottle.hidden = true;
        }
        parts.desc.textContent = product ? this.summary(product.description) : '';
      });
    }

    bottleImage(src) {
      const base = String(src).replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
      const join = base.includes('?') ? '&' : '?';
      const image = element('img');
      image.alt = '';
      image.width = 480;
      image.height = 640;
      image.src = `${base}${join}width=480`;
      image.srcset = `${base}${join}width=240 240w, ${base}${join}width=480 480w, ${base}${join}width=720 720w`;
      image.sizes = '(max-width: 749px) 45vw, 240px';
      return image;
    }

    // A tile's tip for the panel or the similar shades, its photo shown at once (the tile's own may
    // still be waiting to come near the screen)
    copyTip(tip) {
      const copy = tip.cloneNode(true);
      copy.querySelectorAll('[data-tip-lazy]').forEach((nail) => nail.removeAttribute('data-tip-lazy'));
      return copy;
    }

    // A photographed tip's with-flash photo, loaded once; its burst waits for it so the flash shows it
    loadFlash(tip) {
      const nail = tip && tip.querySelector('.cc-tip__nail--has-flash');
      const match = nail && /--tip-flash:\s*url\(['"]?([^'")]+)['"]?\)/.exec(nail.getAttribute('style') || '');
      if (!match) return Promise.resolve();
      if (!this.flashLoads) this.flashLoads = new Map();
      if (!this.flashLoads.has(match[1])) {
        this.flashLoads.set(
          match[1],
          new Promise((resolve) => {
            const image = new Image();
            image.onload = () => resolve();
            image.onerror = () => resolve();
            image.src = match[1];
          })
        );
      }
      return this.flashLoads.get(match[1]);
    }

    quantityOf(variant) {
      return this.cartQty.get(Number(variant)) || 0;
    }

    stockOf(variant) {
      const shade = this.byVariant && this.byVariant.get(Number(variant));
      return shade ? shade.stock : Infinity;
    }

    // True when every unit the store has of this shade is already in the cart.
    isFull(variant) {
      return this.quantityOf(variant) >= this.stockOf(variant);
    }

    stockLimitText() {
      return this.i18n.limitReached || this.i18n.addError;
    }

    renderPanelCart() {
      if (!this.openShade || !this.panelParts) return;
      const shade = this.openShade;
      const parts = this.panelParts;
      const quantity = this.quantityOf(shade.variant);
      const full = shade.available && quantity >= shade.stock;
      if (parts.addLabel) parts.addLabel.textContent = quantity ? this.i18n.viewCartButton || this.i18n.add : this.i18n.add;
      if (parts.add) parts.add.classList.toggle('is-full', full);
      if (parts.stepper) {
        parts.stepper.hidden = quantity === 0;
        parts.qty.textContent = quantity;
        parts.plus.setAttribute('aria-disabled', full ? 'true' : 'false');
      }
      let note = '';
      if (full) note = this.stockLimitText(shade.variant);
      if (parts.inCart) {
        parts.inCart.hidden = !note;
        parts.inCart.textContent = note;
      }
    }

    renderSimilar(shade) {
      const parts = this.panelParts;
      const similar = this.similarTo(shade);
      parts.similar.hidden = similar.length === 0;
      if (!similar.length) {
        parts.similarList.replaceChildren();
        return;
      }
      parts.similarTitle.textContent = shade.available ? this.i18n.similar : this.i18n.similarInStock;
      const addTemplate = this.grid.querySelector('[data-add]');
      parts.similarList.replaceChildren(
        ...similar.map((other) => {
          const item = element('li', 'cc-similar');
          const link = element('a', 'cc-similar__open');
          link.href = other.url;
          link.dataset.similarOpen = other.handle;
          const swatch = element('span', 'cc-similar__swatch');
          if (other.tip) {
            swatch.append(this.copyTip(other.tip));
          } else if (other.img) {
            const image = other.img.cloneNode(true);
            image.removeAttribute('class');
            image.loading = 'eager';
            swatch.append(image);
          }
          link.append(swatch);
          if (other.number) link.append(element('span', 'cc-similar__number', other.number));
          link.append(element('span', 'cc-similar__name', other.name));
          item.append(link);
          if (addTemplate) {
            const button = element('button', 'cc-add cc-add--small');
            button.type = 'button';
            button.dataset.addVariant = String(other.variant);
            button.dataset.addTitle = other.title;
            button.setAttribute('aria-label', fill(this.i18n.addLabel, { title: other.title }));
            button.classList.toggle('is-in-cart', this.quantityOf(other.variant) > 0);
            button.classList.toggle('is-full', this.isFull(other.variant));
            addTemplate.querySelectorAll('.cc-icon').forEach((icon) => button.append(icon.cloneNode(true)));
            item.append(button);
          }
          return item;
        })
      );
    }

    // The closest shades by measured colour, only ones that can be bought now.
    similarTo(shade) {
      if (!shade.lab || !this.similarCount) return [];
      return this.shades
        .filter((other) => other !== shade && other.lab && other.available)
        .map((other) => [other, deltaE(shade.lab, other.lab) + (other.effect === shade.effect ? 0 : 8)])
        .sort((a, b) => a[1] - b[1])
        .slice(0, this.similarCount)
        .map(([other]) => other);
    }

    productData(shade) {
      if (!shade) return Promise.resolve(null);
      if (!this.productCache.has(shade.handle)) {
        const request = fetch(`${shade.url}.js`, { headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null);
        this.productCache.set(shade.handle, request);
      }
      return this.productCache.get(shade.handle);
    }

    // The first sentence or two of the product description; never text in another alphabet.
    summary(html) {
      const text = new DOMParser()
        .parseFromString(String(html || ''), 'text/html')
        .body.textContent.replace(/\s+/g, ' ')
        .trim();
      if (!text) return '';
      if (this.lang !== 'el' && /[\u0370-\u03ff]/.test(text)) return '';
      if (this.lang !== 'ru' && /[\u0400-\u04ff]/.test(text)) return '';
      const sentences = text.match(/[^.!?;]+(?:[.!?;]+|$)/g) || [text];
      let result = '';
      for (const sentence of sentences) {
        if (result && (result + sentence).length > 190) break;
        result += sentence;
      }
      result = result.trim();
      return result.length > 240 ? `${result.slice(0, 237).trimEnd()}…` : result;
    }

    /* Cart */

    cartSections() {
      const miniCart = document.querySelector('mini-cart');
      const sections =
        miniCart && typeof miniCart.getSectionsToRender === 'function' ? miniCart.getSectionsToRender() : CART_SECTIONS;
      return sections.filter((section) => document.getElementById(section.id));
    }

    renderCartSections(sections) {
      if (!sections) return;
      this.cartSections().forEach(({ id, section, selector }) => {
        const target = document.getElementById(id);
        const html = sections[section || id];
        if (!target || !html) return;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector || '.shopify-section');
        if (source) target.innerHTML = source.innerHTML;
      });
      const miniCart = document.querySelector('mini-cart');
      if (miniCart) miniCart.classList.remove('is-empty');
    }

    async add(items, { button = null, title = '' } = {}) {
      if (!items.length || (button && button.getAttribute('aria-busy') === 'true')) return;
      if (button) button.setAttribute('aria-busy', 'true');
      const addUrl = window.theme && theme.routes && theme.routes.cart_add_url ? `${theme.routes.cart_add_url}.js` : cartRoute('cart/add.js');
      const sections = this.cartSections();
      try {
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items,
            sections: sections.map((section) => section.section || section.id),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) throw data;
        this.renderCartSections(data.sections);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'onyx-colour-chart', cart: data });
        }
        // The add response lists each added line: show the result now (no wait for /cart.js) and
        // reconcile with the whole cart in the background.
        if (Array.isArray(data.items)) {
          const added = new Map(items.map((item) => [Number(item.id), item.quantity]));
          if (!this.cartLines) this.cartLines = new Map();
          data.items.forEach((line) => {
            const id = Number(line.variant_id);
            this.cartQty.set(id, Math.max(Number(line.quantity) || 0, this.quantityOf(id) + (added.get(id) || 0)));
            if (line.key) this.cartLines.set(id, line.key);
          });
          this.refreshAddButtons();
          this.renderPanelCart();
          this.renderPalettes();
          this.renderOffer();
        }
        this.fetchCart();
        if (button) {
          button.classList.add('is-added');
          window.setTimeout(() => button.classList.remove('is-added'), 1400);
        }
        const count = items.reduce((sum, item) => sum + item.quantity, 0);
        const message = count > 1 ? fill(this.i18n.addedAll, { count }) : fill(this.i18n.added, { title });
        // The offer line only when something that counts towards it was added.
        let note = '';
        if (this.tiers && this.tiers.length && this.hasOfferShades && this.offerStatusText) {
          const counts = items.some((item) => {
            const shade = this.byVariant && this.byVariant.get(Number(item.id));
            return shade && shade.offer;
          });
          if (counts) note = this.offerStatusText;
        }
        this.showToast(message, { note });
      } catch (error) {
        // Shopify refuses (422) when the cart already holds every unit it can sell: remember that for this
        // shade and say it plainly, without numbers.
        const single = items.length === 1 && this.byVariant ? this.byVariant.get(Number(items[0].id)) : null;
        if (error && error.status === 422 && single && this.quantityOf(single.variant) > 0) {
          single.stock = this.quantityOf(single.variant);
          this.refreshAddButtons();
          this.renderPanelCart();
          this.showToast(this.stockLimitText(single.variant));
        } else {
          const message = (error && (error.description || error.message)) || this.i18n.addError;
          this.showToast(typeof message === 'string' ? message : this.i18n.addError, { error: true });
        }
      } finally {
        if (button) button.removeAttribute('aria-busy');
      }
    }

    async fetchCart() {
      try {
        const response = await fetch(cartRoute('cart.js'), { headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        const cart = await response.json();
        this.applyCart(cart);
        return cart;
      } catch (error) {
        return null;
      }
    }

    applyCart(cart) {
      const quantities = new Map();
      (cart.items || []).forEach((item) => {
        quantities.set(item.variant_id, (quantities.get(item.variant_id) || 0) + item.quantity);
      });
      this.cartQty = quantities;
      this.cartLines = new Map();
      (cart.items || []).forEach((item) => {
        if (!this.cartLines.has(item.variant_id)) this.cartLines.set(item.variant_id, item.key);
      });
      this.refreshAddButtons();
      this.renderPanelCart();
      this.renderPalettes();
      this.renderOffer();
    }

    // A palette whose shades are all in the cart offers the cart instead of adding them again.
    renderPalettes() {
      this.querySelectorAll('[data-add-all]').forEach((button) => {
        if (button.dataset.label === undefined) button.dataset.label = button.textContent.trim();
        const ids = button.dataset.addAll.split(',').map(Number).filter(Boolean);
        const all = ids.length > 0 && ids.every((id) => this.quantityOf(id) > 0);
        button.toggleAttribute('data-in-cart', all);
        const text = all ? this.i18n.viewCartButton || button.dataset.label : button.dataset.label;
        if (button.textContent.trim() !== text) button.textContent = text;
      });
    }

    refreshAddButtons() {
      (this.shades || []).forEach((shade) => {
        const button = shade.addButton;
        if (!button) return;
        const quantity = this.quantityOf(shade.variant);
        const full = quantity >= shade.stock;
        const badge = button.querySelector('[data-qty]');
        if (badge) {
          badge.hidden = quantity === 0 || (full && quantity === 1);
          badge.textContent = quantity || '';
        }
        button.classList.toggle('is-in-cart', quantity > 0);
        button.classList.toggle('is-full', full);
        if (shade.subButton) {
          if (shade.subButton.hidden !== (quantity === 0)) shade.subButton.hidden = quantity === 0;
          shade.subButton.setAttribute('aria-label', fill(this.i18n.removeOneLabel, { title: shade.title }));
        }
        const label = fill(this.i18n.addLabel, { title: shade.title });
        let name = label;
        if (full) name = `${shade.title}. ${this.stockLimitText(shade.variant)}`;
        else if (quantity) name = `${label}. ${fill(this.i18n.inCart, { count: quantity })}`;
        button.setAttribute('aria-label', name);
      });
      if (this.panel) {
        this.panel.querySelectorAll('[data-add-variant]').forEach((button) => {
          const id = Number(button.dataset.addVariant);
          const quantity = this.quantityOf(id);
          button.classList.toggle('is-in-cart', quantity > 0);
          button.classList.toggle('is-full', this.isFull(id));
          const label = fill(this.i18n.addLabel, { title: button.dataset.addTitle || '' });
          button.setAttribute('aria-label', quantity ? `${label}. ${fill(this.i18n.inCart, { count: quantity })}` : label);
        });
      }
    }

    async changeQuantity(variant, quantity, { button = null, title = '' } = {}) {
      const line = this.cartLines && this.cartLines.get(Number(variant));
      if (!line || (button && button.getAttribute('aria-busy') === 'true')) return;
      if (button) button.setAttribute('aria-busy', 'true');
      const changeUrl =
        window.theme && theme.routes && theme.routes.cart_change_url ? `${theme.routes.cart_change_url}.js` : cartRoute('cart/change.js');
      const sections = this.cartSections();
      const focusInside = button && button.contains(document.activeElement);
      try {
        const response = await fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            id: line,
            quantity: Math.max(0, quantity),
            sections: sections.map((section) => section.section || section.id),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) throw data;
        this.renderCartSections(data.sections);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'onyx-colour-chart', cart: data });
        }
        this.applyCart(data);
        const shade = this.byVariant && this.byVariant.get(Number(variant));
        const note =
          shade && shade.offer && this.hasOfferShades && this.tiers && this.tiers.length ? this.offerStatusText : '';
        const text = quantity > 0 ? fill(this.i18n.updated, { title, count: quantity }) : fill(this.i18n.removed, { title });
        this.showToast(text, { note });
        // The control that was pressed may have disappeared (nothing left to remove): keep focus nearby.
        if (focusInside && (button.hidden || button.closest('[hidden]'))) {
          const fallback = shade && this.openShade === shade && this.panelParts ? this.panelParts.add : shade && shade.addButton;
          if (fallback) fallback.focus({ preventScroll: true });
        }
      } catch (error) {
        const message = (error && (error.description || error.message)) || this.i18n.addError;
        this.showToast(typeof message === 'string' ? message : this.i18n.addError, { error: true });
      } finally {
        if (button) button.removeAttribute('aria-busy');
      }
    }

    openCart() {
      this.hideToast();
      const miniCart = document.querySelector('mini-cart');
      if (miniCart && typeof miniCart.open === 'function') miniCart.open();
      else window.location.href = cartRoute('cart');
    }

    /* Offer ladder */

    setupOffer() {
      this.offer = this.querySelector('[data-offer]');
      if (!this.offer) return;
      this.offerSteps = [...this.offer.querySelectorAll('[data-offer-step]')];
      this.tiers = this.offerSteps
        .map((step) => ({ need: Number(step.dataset.offerStep), label: step.dataset.tier }))
        .filter((tier) => tier.need > 0);
      this.offerStatus = this.offer.querySelector('[data-offer-status]');
      if (!this.tiers.length) this.offer.hidden = true;
    }

    renderOffer() {
      if (!this.offer || !this.tiers || !this.tiers.length || !this.shades) return;
      let have = 0;
      this.shades.forEach((shade) => {
        if (shade.offer) have += this.cartQty.get(shade.variant) || 0;
      });
      const tiers = this.tiers;
      const max = tiers[tiers.length - 1].need;
      this.style.setProperty('--cc-progress', Math.min(have / max, 1).toFixed(3));
      let reached = -1;
      tiers.forEach((tier, index) => {
        if (have >= tier.need) reached = index;
      });
      this.offerSteps.forEach((step, index) => {
        step.classList.toggle('is-reached', index <= reached);
        step.classList.toggle('is-next', index === reached + 1);
      });
      let text;
      if (reached < 0) {
        const count = tiers[0].need - have;
        text = have
          ? fill(this.i18n.offerProgress, { have, count, tier: tiers[0].label })
          : fill(this.plural(this.i18n.offerStart, count), { count, tier: tiers[0].label });
      } else if (reached === tiers.length - 1) {
        text = fill(this.i18n.offerMax, { tier: tiers[reached].label });
      } else {
        const next = tiers[reached + 1];
        text = fill(this.i18n.offerReached, { tier: tiers[reached].label, count: next.need - have, next: next.label });
      }
      this.offer.classList.toggle('is-active', have > 0);
      if (this.offerStatus && this.offerStatus.textContent !== text) this.offerStatus.textContent = text;
      this.offerStatusText = text;
      const nextTier = tiers[Math.min(reached + 1, tiers.length - 1)];
      const mini = this.querySelector('[data-offer-mini]');
      if (mini) {
        mini.hidden = have === 0;
        const miniText = mini.querySelector('[data-offer-mini-text]');
        if (miniText && have) {
          miniText.textContent =
            reached === tiers.length - 1
              ? fill(this.i18n.offerMiniHave, { tier: tiers[reached].label })
              : fill(this.i18n.offerMiniNext, { count: nextTier.need - have, tier: nextTier.label });
        }
      }
      const bar = this.querySelector('[data-offer-bar]');
      if (bar) bar.hidden = have === 0;
    }

    /* Restock email */

    async submitNotify(form) {
      const shade = this.openShade;
      const input = form.querySelector('.cc-notify__input');
      const message = form.querySelector('[data-panel-notify-message]');
      const submit = form.querySelector('[type="submit"]');
      if (!shade || !input || !this.notifyForm) return;
      input.value = input.value.trim();
      if (!input.value || !input.checkValidity()) {
        input.setAttribute('aria-invalid', 'true');
        message.textContent = this.i18n.notifyInvalid || this.i18n.notifyError;
        input.focus();
        return;
      }
      input.removeAttribute('aria-invalid');
      const proxy = this.notifyForm;
      proxy.querySelector('[name="contact[email]"]').value = input.value;
      proxy.querySelector('[name="contact[tags]"]').value = `${this.notifyTag},${this.notifyTag}-${shade.handle}`;
      form.setAttribute('aria-busy', 'true');
      if (submit) submit.disabled = true;
      message.textContent = '';
      try {
        const response = await fetch(proxy.action, {
          method: 'POST',
          body: new FormData(proxy),
          headers: { Accept: 'text/html' },
        });
        // A spam check sends the visitor to Shopify's challenge page; submit normally so they can pass it.
        if (response.url.includes('/challenge') || response.status === 403) {
          proxy.submit();
          return;
        }
        if (!response.ok || !response.url.includes('customer_posted=true')) throw new Error('not saved');
        this.notified.add(shade.handle);
        form.classList.add('is-done');
        message.textContent = this.i18n.notifyDone;
      } catch (error) {
        message.textContent = this.i18n.notifyError;
      } finally {
        form.removeAttribute('aria-busy');
        if (submit) submit.disabled = false;
      }
    }

    /* Toast, sticky toolbar, back to top */

    setupToast() {
      if (!this.toast) return;
      const pause = () => window.clearTimeout(this.toastTimer);
      const resume = () => {
        if (!this.toast.hidden) this.toastTimer = window.setTimeout(() => this.hideToast(), 2500);
      };
      this.toast.addEventListener('pointerenter', pause);
      this.toast.addEventListener('pointerleave', resume);
      this.toast.addEventListener('focusin', pause);
      this.toast.addEventListener('focusout', resume);
    }

    showToast(text, { error = false, cart = !error, note = '' } = {}) {
      this.announce(note ? `${text} ${note}` : text);
      if (!this.toast) return;
      this.toast.querySelector('[data-toast-text]').textContent = text;
      const noteNode = this.toast.querySelector('[data-toast-note]');
      if (noteNode) {
        noteNode.textContent = note;
        noteNode.hidden = !note;
      }
      this.toast.classList.toggle('is-error', error);
      const action = this.toast.querySelector('[data-toast-cart]');
      if (action) action.hidden = !cart;
      this.toast.hidden = false;
      window.clearTimeout(this.toastTimer);
      this.toastTimer = window.setTimeout(() => this.hideToast(), error ? 6000 : 4000);
    }

    hideToast() {
      if (!this.toast) return;
      if (this.toast.contains(document.activeElement)) document.activeElement.blur();
      this.toast.hidden = true;
    }

    setupScroll() {
      const header = document.querySelector('.shopify-section-header');
      const updateOffset = () => {
        let top = 0;
        if (
          header &&
          header.classList.contains('shopify-section-header-sticky') &&
          !header.classList.contains('shopify-section-header-hidden')
        ) {
          top = header.offsetHeight;
        }
        if (top !== this.stickyTop) {
          this.stickyTop = top;
          this.style.setProperty('--cc-sticky-top', `${top}px`);
        }
      };
      if (header) {
        this.headerObserver = new MutationObserver(updateOffset);
        this.headerObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
        this.headerResizeObserver = new ResizeObserver(updateOffset);
        this.headerResizeObserver.observe(header);
      }
      updateOffset();

      this.onScrollEvent = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = window.requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.onScroll();
        });
      };
      window.addEventListener('scroll', this.onScrollEvent, { passive: true });
      this.onScroll();
    }

    onScroll() {
      if (this.openDrop && this.placeDrop) this.placeDrop();
      if (this.toolbar && this.browse) {
        const rect = this.toolbar.getBoundingClientRect();
        const stuck = window.scrollY > 0 && rect.top <= (this.stickyTop || 0) + 1 && this.browse.getBoundingClientRect().bottom > rect.bottom + 1;
        this.toolbar.classList.toggle('is-stuck', stuck);
      }
      if (this.topButton) {
        // Shown only while scrolling back up, so it never sits on a shade while the visitor browses down.
        const y = window.scrollY;
        const previous = this.lastScrollY === undefined ? y : this.lastScrollY;
        const far = y > window.innerHeight * 1.5 && this.getBoundingClientRect().bottom > window.innerHeight * 0.5;
        if (!far || y > previous + 4) this.topButton.hidden = true;
        else if (y < previous - 4) this.topButton.hidden = false;
        this.lastScrollY = y;
      }
    }

    /* Events */

    onClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const add = target.closest('[data-add]');
      if (add) {
        event.preventDefault();
        const shade = this.shadeOf(add);
        if (!shade) return;
        if (this.isFull(shade.variant)) this.showToast(this.stockLimitText(shade.variant));
        else this.add([{ id: shade.variant, quantity: 1 }], { button: add, title: shade.title });
        return;
      }
      const flash = target.closest('[data-panel-flash]');
      if (flash) {
        // A photographed tip's with-flash look (phones have no hover)
        const on = flash.getAttribute('aria-pressed') !== 'true';
        flash.setAttribute('aria-pressed', String(on));
        const tip = this.panelParts && this.panelParts.swatch.querySelector('.cc-tip');
        if (!tip) return;
        if (!on) tip.classList.remove('is-flash');
        else {
          this.loadFlash(tip).then(() => {
            if (flash.getAttribute('aria-pressed') === 'true') tip.classList.add('is-flash');
          });
        }
        return;
      }
      const sub = target.closest('[data-sub]');
      if (sub) {
        event.preventDefault();
        const shade = this.shadeOf(sub);
        if (shade) this.changeQuantity(shade.variant, this.quantityOf(shade.variant) - 1, { button: sub, title: shade.title });
        return;
      }
      if (target.closest('[data-panel-sub]')) {
        const shade = this.openShade;
        if (shade) {
          this.changeQuantity(shade.variant, this.quantityOf(shade.variant) - 1, {
            button: target.closest('[data-panel-sub]'),
            title: shade.title,
          });
        }
        return;
      }
      if (target.closest('[data-panel-plus]')) {
        const shade = this.openShade;
        if (shade && this.isFull(shade.variant)) this.showToast(this.stockLimitText(shade.variant));
        else if (shade) this.add([{ id: shade.variant, quantity: 1 }], { button: target.closest('[data-panel-plus]'), title: shade.title });
        return;
      }
      const addVariant = target.closest('[data-add-variant]');
      if (addVariant) {
        const id = Number(addVariant.dataset.addVariant);
        if (this.isFull(id)) this.showToast(this.stockLimitText(id));
        else this.add([{ id, quantity: 1 }], { button: addVariant, title: addVariant.dataset.addTitle });
        return;
      }
      const addAll = target.closest('[data-add-all]');
      if (addAll && addAll.hasAttribute('data-in-cart')) {
        this.openCart();
        return;
      }
      if (addAll) {
        const ids = addAll.dataset.addAll
          .split(',')
          .map(Number)
          .filter((id) => id && !this.isFull(id));
        if (!ids.length) this.showToast(this.i18n.allInCart);
        else this.add(ids.map((id) => ({ id, quantity: 1 })), { button: addAll });
        return;
      }
      if (target.closest('[data-panel-add]')) {
        if (this.openShade && this.quantityOf(this.openShade.variant) > 0) {
          this.openCart();
        } else if (this.openShade) {
          this.add([{ id: this.openShade.variant, quantity: 1 }], {
            button: target.closest('[data-panel-add]'),
            title: this.openShade.title,
          });
        }
        return;
      }
      const notifyOpen = target.closest('[data-notify-open]');
      if (notifyOpen) {
        const shade = this.shadeOf(notifyOpen);
        if (shade === this.openShade) {
          const input = this.panel && this.panel.querySelector('.cc-notify__input');
          if (input && !window.matchMedia('(pointer: coarse)').matches) input.focus();
          else if (input) input.scrollIntoView({ block: 'nearest', behavior: scrollBehaviour() });
        } else {
          this.open(shade, { anchor: true, notify: true });
        }
        return;
      }
      const opener = target.closest('[data-open]');
      if (opener) {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const shade = this.shadeOf(opener);
        if (shade && shade === this.openShade) this.close();
        else this.open(shade, { anchor: true });
        return;
      }
      const similar = target.closest('[data-similar-open]');
      if (similar) {
        const shade = this.byHandle.get(similar.dataset.similarOpen);
        if (!shade || shade.el.hidden || event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        this.open(shade, { reveal: 'nearest', focus: true });
        return;
      }
      if (target.closest('[data-panel-close]')) return this.close();
      if (target.closest('[data-panel-prev]')) return this.step(-1);
      if (target.closest('[data-panel-next]')) return this.step(1);

      const chip = target.closest('[data-filter]');
      if (chip) return this.toggleFilter(chip);
      const sortChip = target.closest('[data-sort-chip]');
      if (sortChip) return this.setSort(sortChip.dataset.sortChip);
      const pill = target.closest('[data-pill]');
      if (pill) return this.removePill(pill);
      if (target.closest('[data-clear]')) {
        this.clearFilters();
        if (!target.closest('[data-sheet]')) this.scrollToResults();
        return;
      }
      if (target.closest('[data-open-sheet]')) return this.openSheet();
      if (target.closest('[data-close-sheet]')) return this.closeSheet();
      if (target.closest('[data-toast-cart]')) {
        this.openCart();
        return;
      }
      if (target.closest('[data-top]')) {
        window.scrollTo({ top: 0, behavior: scrollBehaviour() });
        if (event.detail === 0 && this.search) this.search.focus({ preventScroll: true });
      }
    }

    onKeydown(event) {
      if (!this.openShade || !this.panel) return;
      const inPanel = this.panel.contains(event.target);
      const onTile = this.openShade.link && event.target === this.openShade.link;
      if (!inPanel && !onTile) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        const link = this.openShade.link;
        this.close();
        if (link) link.focus({ preventScroll: true });
        return;
      }
      if (event.target.matches('input, textarea, select')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(event.key === 'ArrowLeft' ? -1 : 1);
      }
    }

    onSubmit(event) {
      const form = event.target.closest('[data-panel-notify]');
      if (!form) return;
      event.preventDefault();
      this.submitNotify(form);
    }
  }

  customElements.define('onyx-colour-chart', OnyxColourChart);
})();
